import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { KeyRound, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const strength =
    pw1.length >= 12 && /[A-Z]/.test(pw1) && /[a-z]/.test(pw1) && /\d/.test(pw1) && /[^A-Za-z0-9]/.test(pw1)
      ? "strong"
      : pw1.length >= 8
      ? "ok"
      : pw1.length > 0
      ? "weak"
      : "";

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw1 !== pw2) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      toast.success("Password set — you can sign in now");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md space-y-3 border-rose-200 bg-white p-6 text-center shadow-sm" data-testid="reset-missing-token">
          <ShieldAlert className="mx-auto h-8 w-8 text-rose-500" />
          <h1 className="text-lg font-semibold text-slate-800">Invalid reset link</h1>
          <p className="text-sm text-slate-500">This link is missing its token. Ask your admin for a fresh welcome email.</p>
          <Link to="/login" className="inline-block text-sm font-medium text-primary hover:underline" data-testid="reset-go-login">Back to sign in</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-[#eef2fb] p-6">
      <Card className="w-full max-w-sm border-slate-200 bg-white p-6 shadow-sm" data-testid="reset-password-card">
        <div className="mb-4 flex items-center gap-2 text-primary">
          <KeyRound className="h-5 w-5" />
          <h1 className="text-lg font-semibold text-slate-800">Set your password</h1>
        </div>
        {done ? (
          <div className="space-y-3 text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
            <p className="text-sm text-slate-600">Password saved. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-xs text-slate-500">
              Welcome to iplogin. Choose a password with at least 8 characters — we recommend 12+ with mixed case, a number, and a symbol.
            </p>
            <div className="space-y-1">
              <Label htmlFor="pw1" className="text-xs">New password</Label>
              <Input id="pw1" data-testid="reset-password-input" type="password" autoComplete="new-password"
                value={pw1} onChange={(e) => setPw1(e.target.value)} required minLength={8} />
              {strength && (
                <div className="flex items-center gap-1.5 text-[11px]" data-testid={`reset-strength-${strength}`}>
                  <span className={`inline-block h-1.5 w-16 rounded-full ${
                    strength === "strong" ? "bg-emerald-400" : strength === "ok" ? "bg-amber-400" : "bg-rose-400"}`} />
                  <span className={strength === "strong" ? "text-emerald-600" : strength === "ok" ? "text-amber-600" : "text-rose-600"}>
                    {strength === "strong" ? "Strong" : strength === "ok" ? "Okay" : "Too short"}
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw2" className="text-xs">Confirm password</Label>
              <Input id="pw2" data-testid="reset-password-confirm" type="password" autoComplete="new-password"
                value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} />
            </div>
            <Button type="submit" disabled={submitting} data-testid="reset-submit"
              className="mt-1 w-full bg-primary hover:bg-primary/90 text-primary-foreground">
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save and sign in
            </Button>
            <p className="pt-1 text-center text-xs text-slate-400">This link expires 24 hours after it was sent.</p>
          </form>
        )}
      </Card>
    </div>
  );
}
